/**
 * Cranial geometry for the photographic cranial analysis tool.
 *
 * Pure, deterministic functions: no React, no DOM, no side effects.
 * computeMeasurements receives and returns normalized points (0..1 relative to the image
 * width/height); every calculation runs internally in pixels so that angles and distances
 * are not distorted on non-square images.
 *
 * Protocol (CRANIAL_PROTOCOL v2.1, Skully Care-like):
 * - landmarks: the nose, the anterior helical border of each ear and, optionally, a
 *   posterior point P placed by the user;
 * - axis (automatic P, default): line through the ear midpoint with direction
 *   (nose - ear midpoint); cranial length A-P, where A is the axis crossing with the contour
 *   on the nose side and P the crossing on the opposite side (the farthest on each side);
 * - axis (manual P): line through the nose and P; P is the landmark itself and A the
 *   crossing of that line with the contour farthest towards the nose; length = |A - P|;
 * - cranial width: distance between the two ear points (no perpendicularity constraint);
 * - center: midpoint of A-P;
 * - diagonals: chords through the center at +/- `diagonalAngleDegrees` from the axis;
 * - CVA = |A - B|; CVAI = |A - B| / min(A, B) * 100; CI = width / length * 100.
 * The contour is a closed polygon (the last point connects to the first).
 */

export const CRANIAL_FORMULA = { name: 'CVAI_SHORT_DIAGONAL', version: '1.0' };

export const CRANIAL_PROTOCOL = {
  version: '2.1',
  name: 'SKULLY_CARE_LIKE',
  diagonalAngleDegrees: 30,
  centerDefinition: 'midpoint_of_cranial_length',
  // Automatic P: axis through the nose and the ear midpoint, A and P are its contour
  // crossings. Manual P: axis through the nose and P, A is the nose-side contour crossing.
  lengthDefinition: 'nose_side_crossing_to_p_where_p_is_manual_or_the_ear_midpoint_axis_crossing',
  widthDefinition: 'ear_to_ear_anterior_helix',
};

const EPSILON = 1e-9;
// Intersections closer than this (in direction units) are the same point (e.g. a vertex hit).
const MERGE_TOLERANCE = 1e-6;

const isFiniteNumber = (value) => typeof value === 'number' && Number.isFinite(value);
const isPositiveNumber = (value) => isFiniteNumber(value) && value > 0;
const isPoint = (p) => !!p && isFiniteNumber(p.x) && isFiniteNumber(p.y);
const cross = (a, b) => a.x * b.y - a.y * b.x;
const dot = (a, b) => a.x * b.x + a.y * b.y;
const midpoint = (a, b) => ({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 });
const rotate = (v, radians) => ({
  x: v.x * Math.cos(radians) - v.y * Math.sin(radians),
  y: v.x * Math.sin(radians) + v.y * Math.cos(radians),
});

/**
 * Converts a pixel point to a normalized point (0..1 relative to the image size).
 * @param {{x:number,y:number}} point Pixel point.
 * @param {number} width Image width in pixels.
 * @param {number} height Image height in pixels.
 * @returns {{x:number,y:number}|null} Normalized point, or null on invalid input.
 */
export function normalizePoint(point, width, height) {
  if (!isPoint(point) || !isPositiveNumber(width) || !isPositiveNumber(height)) return null;
  return { x: point.x / width, y: point.y / height };
}

/**
 * Converts a normalized point (0..1) to image pixels.
 * @param {{x:number,y:number}} point Normalized point.
 * @param {number} width Image width in pixels.
 * @param {number} height Image height in pixels.
 * @returns {{x:number,y:number}|null} Pixel point, or null on invalid input.
 */
export function denormalizePoint(point, width, height) {
  if (!isPoint(point) || !isPositiveNumber(width) || !isPositiveNumber(height)) return null;
  return { x: point.x * width, y: point.y * height };
}

/**
 * Euclidean distance between two points in the same coordinate space.
 * @param {{x:number,y:number}} a
 * @param {{x:number,y:number}} b
 * @returns {number} Distance, or NaN on invalid input.
 */
export function distance(a, b) {
  if (!isPoint(a) || !isPoint(b)) return NaN;
  return Math.hypot(b.x - a.x, b.y - a.y);
}

/**
 * Area centroid of a closed polygon. Falls back to the vertex mean for degenerate
 * (zero-area) polygons.
 * @param {Array<{x:number,y:number}>} contourPx Contour in pixels.
 * @returns {{x:number,y:number}|null} Centroid, or null for an empty/invalid contour.
 */
export function calculateContourCentroid(contourPx) {
  const points = Array.isArray(contourPx) ? contourPx.filter(isPoint) : [];
  if (points.length === 0) return null;
  let area2 = 0;
  let cx = 0;
  let cy = 0;
  for (let i = 0; i < points.length; i += 1) {
    const p = points[i];
    const q = points[(i + 1) % points.length];
    const c = cross(p, q);
    area2 += c;
    cx += (p.x + q.x) * c;
    cy += (p.y + q.y) * c;
  }
  if (Math.abs(area2) > EPSILON) {
    return { x: cx / (3 * area2), y: cy / (3 * area2) };
  }
  const sum = points.reduce((acc, p) => ({ x: acc.x + p.x, y: acc.y + p.y }), { x: 0, y: 0 });
  return { x: sum.x / points.length, y: sum.y / points.length };
}

/**
 * Builds an anterior->posterior axis from two points on it (protocol v2: the nose and the
 * ear midpoint).
 * @param {{x:number,y:number}} anteriorPx Front point in pixels.
 * @param {{x:number,y:number}} posteriorPx Back point in pixels.
 * @returns {{origin:{x:number,y:number},direction:{x:number,y:number},normal:{x:number,y:number},lengthPx:number}|null}
 *   origin = anterior, direction = unit vector anterior->posterior, normal = direction
 *   rotated by +90 degrees. Null when the landmarks are invalid or coincide.
 */
export function calculateAnteriorPosteriorAxis(anteriorPx, posteriorPx) {
  if (!isPoint(anteriorPx) || !isPoint(posteriorPx)) return null;
  const lengthPx = distance(anteriorPx, posteriorPx);
  if (!(lengthPx > EPSILON)) return null;
  const direction = {
    x: (posteriorPx.x - anteriorPx.x) / lengthPx,
    y: (posteriorPx.y - anteriorPx.y) / lengthPx,
  };
  return {
    origin: { x: anteriorPx.x, y: anteriorPx.y },
    direction,
    normal: { x: -direction.y, y: direction.x },
    lengthPx,
  };
}

/**
 * Intersections of the infinite line `pointPx + t * directionPx` with the closed contour.
 * Collinear edges contribute both endpoints; near-duplicate hits (shared vertices) are merged.
 * @param {{x:number,y:number}} pointPx Point on the line (pixels).
 * @param {{x:number,y:number}} directionPx Line direction (need not be unit).
 * @param {Array<{x:number,y:number}>} contourPx Closed contour in pixels.
 * @returns {Array<{x:number,y:number,t:number}>} Intersections sorted by t (empty on invalid input).
 */
export function calculateLineContourIntersections(pointPx, directionPx, contourPx) {
  if (!isPoint(pointPx) || !isPoint(directionPx) || !Array.isArray(contourPx)) return [];
  const dirLength = Math.hypot(directionPx.x, directionPx.y);
  if (!(dirLength > EPSILON)) return [];
  const points = contourPx.filter(isPoint);
  if (points.length < 2) return [];

  const ts = [];
  for (let i = 0; i < points.length; i += 1) {
    const p = points[i];
    const q = points[(i + 1) % points.length];
    const edge = { x: q.x - p.x, y: q.y - p.y };
    const edgeLength = Math.hypot(edge.x, edge.y);
    if (!(edgeLength > EPSILON)) continue;
    const offset = { x: p.x - pointPx.x, y: p.y - pointPx.y };
    const denom = cross(directionPx, edge);
    // Relative parallel test so the tolerance does not depend on the image scale.
    if (Math.abs(denom) <= EPSILON * dirLength * edgeLength) {
      // Parallel edge: it only intersects when collinear; keep both endpoints of the overlap.
      const offsetLength = Math.max(1, Math.hypot(offset.x, offset.y));
      if (Math.abs(cross(offset, directionPx)) <= 1e-7 * dirLength * offsetLength) {
        const dd = dirLength * dirLength;
        ts.push(dot(offset, directionPx) / dd);
        ts.push(dot({ x: q.x - pointPx.x, y: q.y - pointPx.y }, directionPx) / dd);
      }
      continue;
    }
    const t = cross(offset, edge) / denom;
    const s = cross(offset, directionPx) / denom;
    if (s >= -EPSILON && s <= 1 + EPSILON) ts.push(t);
  }

  ts.sort((a, b) => a - b);
  const merged = [];
  ts.forEach((t) => {
    if (merged.length === 0 || Math.abs(t - merged[merged.length - 1]) > MERGE_TOLERANCE) {
      merged.push(t);
    }
  });
  return merged.map((t) => ({
    x: pointPx.x + t * directionPx.x,
    y: pointPx.y + t * directionPx.y,
    t,
  }));
}

/** Chord between the first and last intersections of a line with the contour, or null. */
function farthestChord(pointPx, directionPx, contourPx) {
  const hits = calculateLineContourIntersections(pointPx, directionPx, contourPx);
  if (hits.length < 2) return null;
  const first = hits[0];
  const last = hits[hits.length - 1];
  const start = { x: first.x, y: first.y };
  const end = { x: last.x, y: last.y };
  const lengthPx = distance(start, end);
  if (!(lengthPx > EPSILON)) return null;
  return { start, end, lengthPx };
}

/**
 * Cranial length: chord of the AP axis line between its two farthest contour intersections.
 * The line is infinite, so the result does not depend on where the axis origin lies.
 * @param {Array<{x:number,y:number}>} contourPx Closed contour in pixels.
 * @param {ReturnType<typeof calculateAnteriorPosteriorAxis>} axis AP axis.
 * @returns {{start:{x:number,y:number},end:{x:number,y:number},lengthPx:number}|null}
 *   start is the anterior end (A, nose side), end the posterior end (P); null when the
 *   axis misses the contour.
 */
export function calculateCranialLength(contourPx, axis) {
  if (!axis || !isPoint(axis.origin) || !isPoint(axis.direction)) return null;
  return farthestChord(axis.origin, axis.direction, contourPx);
}

/**
 * Cranial length with a manual posterior point: A is the crossing of the nose-P line with
 * the contour farthest towards the nose (before P), P is the landmark itself.
 * @param {Array<{x:number,y:number}>} contourPx Closed contour in pixels.
 * @param {ReturnType<typeof calculateAnteriorPosteriorAxis>} axis Axis from the nose to P.
 * @param {{x:number,y:number}} posteriorPx Manual P in pixels.
 * @returns {{start:{x:number,y:number},end:{x:number,y:number},lengthPx:number}|null}
 *   Null when the line has no contour crossing on the nose side of P.
 */
export function calculateCranialLengthToPoint(contourPx, axis, posteriorPx) {
  if (!axis || !isPoint(axis.origin) || !isPoint(axis.direction) || !isPoint(posteriorPx)) {
    return null;
  }
  const hits = calculateLineContourIntersections(axis.origin, axis.direction, contourPx);
  const tPosterior = dot(
    { x: posteriorPx.x - axis.origin.x, y: posteriorPx.y - axis.origin.y },
    axis.direction,
  );
  const front = hits.find((hit) => hit.t < tPosterior - MERGE_TOLERANCE);
  if (!front) return null;
  const start = { x: front.x, y: front.y };
  const end = { x: posteriorPx.x, y: posteriorPx.y };
  const lengthPx = distance(start, end);
  if (!(lengthPx > EPSILON)) return null;
  return { start, end, lengthPx };
}

/**
 * Diagonals: chords through the center at +angleDeg and -angleDeg from the AP axis.
 * Each chord starts at its anterior (front) end. diagonalA is the diagonal whose front end
 * has the larger x in the image (right side); on an exact tie, the one with the larger y.
 * @param {Array<{x:number,y:number}>} contourPx Closed contour in pixels.
 * @param {ReturnType<typeof calculateAnteriorPosteriorAxis>} axis AP axis.
 * @param {{x:number,y:number}} centerPx Center (midpoint of the cranial length).
 * @param {number} angleDeg Angle from the AP axis in degrees (protocol: 30).
 * @returns {{diagonalA:{start:object,end:object,lengthPx:number},diagonalB:{start:object,end:object,lengthPx:number}}|null}
 */
export function calculateDiagonalIntersections(contourPx, axis, centerPx, angleDeg) {
  if (!axis || !isPoint(axis.direction) || !isPoint(centerPx) || !isFiniteNumber(angleDeg)) {
    return null;
  }
  const radians = (angleDeg * Math.PI) / 180;
  // Front end = smaller projection on the AP direction (closer to the anterior landmark).
  const frontFirst = (chord) => {
    if (!chord) return null;
    return dot(chord.start, axis.direction) <= dot(chord.end, axis.direction)
      ? chord
      : { start: chord.end, end: chord.start, lengthPx: chord.lengthPx };
  };
  const first = frontFirst(farthestChord(centerPx, rotate(axis.direction, radians), contourPx));
  const second = frontFirst(farthestChord(centerPx, rotate(axis.direction, -radians), contourPx));
  if (!first || !second) return null;

  const dx = first.start.x - second.start.x;
  const firstIsA = Math.abs(dx) > EPSILON ? dx > 0 : first.start.y >= second.start.y;
  return firstIsA
    ? { diagonalA: first, diagonalB: second }
    : { diagonalA: second, diagonalB: first };
}

/**
 * Cranial vault asymmetry: |A - B| (same unit as the inputs).
 * @param {number} a Diagonal A.
 * @param {number} b Diagonal B.
 * @returns {number|null}
 */
export function calculateCva(a, b) {
  if (!isFiniteNumber(a) || !isFiniteNumber(b)) return null;
  return Math.abs(a - b);
}

/**
 * Cranial vault asymmetry index (CVAI_SHORT_DIAGONAL v1.0, as in Skully Care):
 * |A - B| / min(A, B) * 100.
 * @param {number} a Diagonal A.
 * @param {number} b Diagonal B.
 * @returns {number|null} Percentage, or null when the shorter diagonal is not positive.
 */
export function calculateCvai(a, b) {
  if (!isFiniteNumber(a) || !isFiniteNumber(b)) return null;
  const shorter = Math.min(a, b);
  if (!(shorter > 0)) return null;
  return (Math.abs(a - b) / shorter) * 100;
}

/**
 * Cephalic index: width / length * 100.
 * @param {number} widthPx Cranial width.
 * @param {number} lengthPx Cranial length (same unit as the width).
 * @returns {number|null} Percentage, or null when the length is not positive.
 */
export function calculateCephalicIndex(widthPx, lengthPx) {
  if (!isFiniteNumber(widthPx) || !isPositiveNumber(lengthPx)) return null;
  return (widthPx / lengthPx) * 100;
}

/**
 * Pixels per millimeter from two normalized reference points and the real reference size.
 * @param {Array<{x:number,y:number}>} pointsNorm Normalized points (the first two are used).
 * @param {number} referenceSizeMm Real distance between them in millimeters.
 * @param {number} width Image width in pixels.
 * @param {number} height Image height in pixels.
 * @returns {number|null}
 */
export function calculatePixelsPerMillimeter(pointsNorm, referenceSizeMm, width, height) {
  if (!Array.isArray(pointsNorm) || pointsNorm.length < 2 || !isPositiveNumber(referenceSizeMm)) {
    return null;
  }
  const a = denormalizePoint(pointsNorm[0], width, height);
  const b = denormalizePoint(pointsNorm[1], width, height);
  if (!a || !b) return null;
  const px = distance(a, b);
  if (!(px > EPSILON)) return null;
  return px / referenceSizeMm;
}

/**
 * Converts a pixel measurement to millimeters.
 * @param {number} px Measurement in pixels.
 * @param {number} pixelsPerMillimeter Calibration factor.
 * @returns {number|null} Millimeters, or null when either input is invalid.
 */
export function convertPixelsToMillimeters(px, pixelsPerMillimeter) {
  if (!isFiniteNumber(px) || !isPositiveNumber(pixelsPerMillimeter)) return null;
  return px / pixelsPerMillimeter;
}

const fail = (error) => ({ ok: false, error });

/**
 * Computes every cranial measurement from normalized landmarks and contour (protocol v2.1).
 * landmarks.posterior is optional: null/undefined uses the automatic P (ear midpoint rule),
 * a point uses the manual P (axis through the nose and P).
 * Never throws: invalid input yields `{ ok:false, error }`.
 * Error mapping: missing contour or fewer than 3 valid points -> CONTOUR_TOO_SHORT;
 * invalid image size, a missing landmark, a nose on the ear midpoint (or on the manual P), an
 * axis that misses the contour or a manual P with no crossing before it -> NO_AXIS_INTERSECTION; coincident ear points -> NO_WIDTH; a missing
 * diagonal -> NO_DIAGONAL. Non-finite contour points are ignored.
 * @param {{landmarks:{nose:{x:number,y:number},earLeft:{x:number,y:number},earRight:{x:number,y:number},
 *   posterior?:{x:number,y:number}|null},
 *   cranialContour:Array<{x:number,y:number}>, calibration?:{pixelsPerMillimeter?:number}|null,
 *   imageWidth:number, imageHeight:number}} input
 * @returns {object} `{ ok:true, lines, derived, px, mm, indices, formula, protocol }` or
 *   `{ ok:false, error }`. Lines and derived points (`anterior`, `posterior`, `earMidpoint`)
 *   are normalized (0..1) for the overlay, with `posteriorSource` 'auto' | 'manual'; lines.width goes from earLeft to earRight;
 *   px/mm are measurements; mm is null without a valid calibration.pixelsPerMillimeter.
 */
export function computeMeasurements(input) {
  const { landmarks, cranialContour, calibration, imageWidth, imageHeight } = input || {};
  const contourNorm = Array.isArray(cranialContour) ? cranialContour.filter(isPoint) : [];
  if (contourNorm.length < 3) return fail('CONTOUR_TOO_SHORT');
  if (!isPositiveNumber(imageWidth) || !isPositiveNumber(imageHeight)) {
    return fail('NO_AXIS_INTERSECTION');
  }

  const toPx = (p) => denormalizePoint(p, imageWidth, imageHeight);
  const toNorm = (p) => normalizePoint(p, imageWidth, imageHeight);
  const lineToNorm = (line) => ({ start: toNorm(line.start), end: toNorm(line.end) });

  const nosePx = toPx(landmarks && landmarks.nose);
  const earLeftPx = toPx(landmarks && landmarks.earLeft);
  const earRightPx = toPx(landmarks && landmarks.earRight);
  if (!nosePx || !earLeftPx || !earRightPx) return fail('NO_AXIS_INTERSECTION');

  const contourPx = contourNorm.map(toPx);
  const earMidPx = midpoint(earLeftPx, earRightPx);
  const posteriorPx = landmarks.posterior ? toPx(landmarks.posterior) : null;
  if (landmarks.posterior && !posteriorPx) return fail('NO_AXIS_INTERSECTION');

  let axis;
  let length;
  if (posteriorPx) {
    // Manual P: the axis is the nose-P line, P is the landmark and A the nose-side crossing.
    axis = calculateAnteriorPosteriorAxis(nosePx, posteriorPx);
    if (!axis) return fail('NO_AXIS_INTERSECTION');
    length = calculateCranialLengthToPoint(contourPx, axis, posteriorPx);
  } else {
    // Automatic P: the nose only gives the direction; the axis is the line through the ear
    // midpoint, and A/P are its farthest contour crossings on each side.
    axis = calculateAnteriorPosteriorAxis(nosePx, earMidPx);
    if (!axis) return fail('NO_AXIS_INTERSECTION');
    length = calculateCranialLength(contourPx, axis);
  }
  if (!length) return fail('NO_AXIS_INTERSECTION');
  const centerPx = midpoint(length.start, length.end);

  const widthPx = distance(earLeftPx, earRightPx);
  if (!(widthPx > EPSILON)) return fail('NO_WIDTH');
  const width = { start: earLeftPx, end: earRightPx, lengthPx: widthPx };

  const diagonals = calculateDiagonalIntersections(
    contourPx,
    axis,
    centerPx,
    CRANIAL_PROTOCOL.diagonalAngleDegrees,
  );
  if (!diagonals) return fail('NO_DIAGONAL');

  const px = {
    length: length.lengthPx,
    width: width.lengthPx,
    diagonalA: diagonals.diagonalA.lengthPx,
    diagonalB: diagonals.diagonalB.lengthPx,
    cva: calculateCva(diagonals.diagonalA.lengthPx, diagonals.diagonalB.lengthPx),
  };

  const ppm = calibration ? calibration.pixelsPerMillimeter : null;
  const mm = isPositiveNumber(ppm)
    ? {
        length: convertPixelsToMillimeters(px.length, ppm),
        width: convertPixelsToMillimeters(px.width, ppm),
        diagonalA: convertPixelsToMillimeters(px.diagonalA, ppm),
        diagonalB: convertPixelsToMillimeters(px.diagonalB, ppm),
        cva: convertPixelsToMillimeters(px.cva, ppm),
      }
    : null;

  return {
    ok: true,
    lines: {
      apAxis: lineToNorm(length),
      width: lineToNorm(width),
      diagonalA: lineToNorm(diagonals.diagonalA),
      diagonalB: lineToNorm(diagonals.diagonalB),
      center: toNorm(centerPx),
    },
    derived: {
      anterior: toNorm(length.start),
      posterior: toNorm(length.end),
      earMidpoint: toNorm(earMidPx),
      posteriorSource: posteriorPx ? 'manual' : 'auto',
    },
    px,
    mm,
    indices: {
      cvai: calculateCvai(px.diagonalA, px.diagonalB),
      ci: calculateCephalicIndex(px.width, px.length),
    },
    formula: CRANIAL_FORMULA,
    protocol: CRANIAL_PROTOCOL,
  };
}
